import * as React from 'react';
import { t } from '@reos/i18n';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Image as ImageIcon, Search } from 'lucide-react';
import { DataTable, EmptyState, type DataTableColumn, type DataTableTexts, type ListQuery } from '@redhand/ui';
import {
  downloadPropertiesCsv,
  errorMessage,
  fetchLocalities,
  fetchPropertiesList,
  formatDate,
  formatMoney,
  kindLabel,
  KIND_VALUES,
  LISTING_STAGES,
  mediaSrc,
  stageLabel,
  type PropertyListItem,
} from '../lib/properties.js';
import { startAuditBatch } from '../lib/audit-api.js';
import { fetchRegistry, fetchSyncCenter } from '../lib/connectors.js';
import {
  portalExportByProperty,
  type PortalExportSummary,
  type PortalRunState,
} from '../lib/portal-export-summary.js';
import { MapPreview } from '../components/MapPreview.js';
import { PageHead } from '../components/page-head.js';
import { BulkPortalActions } from '../components/BulkPortalActions.js';

/**
 * Seznam nemovitosti nad BFF `GET /v1/properties/list`
 * (apps/api/src/routes/properties-list.ts, LIST-CONTRACT). Krok S3: obrazovka
 * uz necte staticky import verejnych nabidek ze slozky `public/` — kazdy
 * radek je nemovitost tenanta z DB, se strankovanim, razenim a exportem
 * na serveru.
 */

const TABLE_TEXTS: DataTableTexts = {
  paginationLabel: t('webAdmin.zakazky.table.paginationLabel', { locale: 'cs' }),
  pageStatus: (page, pageCount, total, pageSize) => {
    const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);
    return t('webAdmin.zakazky.table.pageStatus', {
      locale: 'cs',
      values: {
        from: String(from),
        to: String(to),
        total: String(total),
        page: String(page),
        pageCount: String(pageCount),
      },
    });
  },
  prev: t('webAdmin.zakazky.table.prev', { locale: 'cs' }),
  next: t('webAdmin.zakazky.table.next', { locale: 'cs' }),
  loading: t('webAdmin.mvListings.table.loading', { locale: 'cs' }),
  errorTitle: t('webAdmin.mvListings.table.errorTitle', { locale: 'cs' }),
  retry: t('webAdmin.zakazky.table.retry', { locale: 'cs' }),
  exportCsv: t('webAdmin.zakazky.table.exportCsv', { locale: 'cs' }),
  rowsPerPage: t('webAdmin.caseListTexts.rowsPerPage', { locale: 'cs' }),
  pageButtonLabel: (page) =>
    t('webAdmin.zakazky.table.pageButtonLabel', { locale: 'cs', values: { page: String(page) } }),
};

const PAGE_SIZE_OPTIONS = [12, 24, 48] as const;
const DEFAULT_QUERY: ListQuery = { page: 1, pageSize: 24 };

/**
 * Krok SEZ1 bod 1 — tri lidske stavy portalu ze zadani (bezi / ceka / chyba)
 * a ctvrty pro portal, kde se nic nedeje. Rozhoduje `portalRunState`, tady
 * jsou jen popisky.
 */
const PORTAL_RUN_STATE_LABELS: Readonly<Record<PortalRunState, string>> = {
  running: t('webAdmin.mvListings.portalRunning', { locale: 'cs' }),
  waiting: t('webAdmin.mvListings.portalWaiting', { locale: 'cs' }),
  error: t('webAdmin.mvListings.portalError', { locale: 'cs' }),
  off: t('webAdmin.mvListings.portalOff', { locale: 'cs' }),
};

/**
 * Odznak souhrnu nese barvu NEJHORSIHO stavu, ktery radek ma: chyba exportu
 * je to jedine, co makler musi z prehledu poznat bez rozbaleni.
 */
function portalBadgeClass(summary: PortalExportSummary): string {
  if (summary.rows.some((row) => row.runState === 'error')) return 'rh-badge rh-badge--danger';
  if (summary.published > 0) return 'rh-badge rh-badge--success';
  if (summary.rows.some((row) => row.runState === 'waiting')) return 'rh-badge rh-badge--warning';
  return 'rh-badge rh-badge--neutral';
}

function firstFilterValue(filters: Readonly<Record<string, unknown>> | undefined, key: string): string {
  const value = filters?.[key];
  if (Array.isArray(value)) return String(value[0] ?? '');
  return value !== undefined ? String(value) : '';
}

export function NemovitostiPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = React.useState<ListQuery>(DEFAULT_QUERY);
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(new Set());
  const [batchState, setBatchState] = React.useState<
    { readonly kind: 'idle' } | { readonly kind: 'submitting' } | { readonly kind: 'error'; readonly message: string }
  >({ kind: 'idle' });

  const list = useQuery({
    queryKey: ['properties-list', query],
    queryFn: () => fetchPropertiesList(query),
  });
  const localities = useQuery({ queryKey: ['property-localities'], queryFn: fetchLocalities });

  /*
   * Krok SEZ1 bod 1 — STAV EXPORTU PRIMO V SEZNAMU (R.B. 29. 10., bod 8).
   *
   * Zadna nova routa: ctou se tytez dve odpovedi, ze kterych zije obrazovka
   * Portaly (registr portalu + matice publikaci). `connector-registry` je
   * navic tentyz klic, jaky uz na teto obrazovce nacita karta hromadnych
   * akci — react-query ho vrati z cache, ne druhym dotazem.
   *
   * Chyba nebo nedostupna matice NESMI shodit seznam nemovitosti: sloupec
   * pak zustane prazdny (`portalsByProperty` je prazdna mapa), radky jedou dal.
   */
  const registry = useQuery({ queryKey: ['connector-registry'], queryFn: fetchRegistry });
  const syncCenter = useQuery({ queryKey: ['publications-sync-center'], queryFn: fetchSyncCenter });
  const portalsByProperty = React.useMemo(() => {
    if (syncCenter.data === undefined || registry.data === undefined) {
      return new Map<string, PortalExportSummary>();
    }
    return portalExportByProperty(
      syncCenter.data,
      registry.data.filter((portal) => portal.connectorImplemented),
    );
  }, [syncCenter.data, registry.data]);

  const toggleSelected = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleStartAuditBatch = (): void => {
    setBatchState({ kind: 'submitting' });
    void startAuditBatch([...selected])
      .then(() => {
        setSelected(new Set());
        setBatchState({ kind: 'idle' });
        void navigate({ to: '/audit' });
      })
      .catch((error: unknown) => {
        setBatchState({ kind: 'error', message: error instanceof Error ? error.message : String(error) });
      });
  };

  const patchQuery = (patch: Partial<ListQuery>): void => {
    setQuery((prev) => ({ ...prev, page: 1, ...patch }));
  };

  /**
   * Krok UXF2 bod 5 (nalez #24): filtr uz neni jen obec — vedle nej stoji
   * DRUH a STAV inzerce. Server je uz umel (`filter[kind]`, `filter[stage]`
   * v `apps/api/src/routes/properties-list.ts`), chybely jen v UI.
   * Jedna funkce pro vsechny tri filtry, aby se nastaveni nerozeslo.
   */
  const setFilter = (key: 'locality' | 'kind' | 'stage', value: string): void => {
    const filters: Record<string, string> = { ...(query.filters as Record<string, string> | undefined) };
    if (value === '') delete filters[key];
    else filters[key] = value;
    patchQuery({ filters });
  };

  const localityFilter = firstFilterValue(query.filters, 'locality');
  const kindFilter = firstFilterValue(query.filters, 'kind');
  const stageFilter = firstFilterValue(query.filters, 'stage');
  const searchValue = query.search ?? '';

  const openDetail = (id: string): void => {
    void navigate({ to: '/nemovitosti/$id', params: { id } });
  };

  /**
   * Krok UXF2 bod 5 (nalez #24) — FOTKA PATRI K NAZVU.
   *
   * Do tohoto kroku stal nahled v samostatnem prvnim sloupci a mezi nim
   * a nazvem lezel sloupec zaskrtavatek, takze oko skakalo. Teď je poradi
   * zaskrtavatko → nahled+nazev v JEDNE bunce → druh → stav → cena.
   */
  const cover = (row: PropertyListItem): React.JSX.Element =>
    row.coverPhotoUrl === null ? (
      row.lat !== null && row.lon !== null ? (
        <span
          className="block shrink-0 overflow-hidden rounded"
          style={{ width: 96, height: 72 }}
          title={t('webAdmin.gallery.noCoverMapFallback', { locale: 'cs' })}
          data-testid={`nemovitosti-cover-map-${row.id}`}
        >
          <MapPreview lat={row.lat} lon={row.lon} />
        </span>
      ) : (
        /*
         * Oprava e2e 2 kroku POMLC1: `aria-label` na `<span>` BEZ role je
         * `aria-prohibited-attr` (axe serious, 12x na `/nemovitosti`) —
         * span nema implicitni roli, takze mu pristupne jmeno nepatri.
         * `role="img"` z nahledu dela obrazek, jmeno tim plati a ctecka
         * ohlasi „Bez fotky" misto ticha. Mapovy nahled vedle role nema:
         * nese jen `title`, zadne `aria-*`, takze ho pravidlo netrapi.
         */
        <span
          role="img"
          className="flex shrink-0 items-center justify-center rounded"
          style={{ width: 96, height: 72, background: 'var(--rh-canvas)', color: 'var(--rh-text-muted)' }}
          title={t('webAdmin.gallery.noCover', { locale: 'cs' })}
          aria-label={t('webAdmin.gallery.noCover', { locale: 'cs' })}
          data-testid={`nemovitosti-cover-empty-${row.id}`}
        >
          <ImageIcon size={20} aria-hidden="true" />
        </span>
      )
    ) : (
      <img
        src={mediaSrc(row.coverPhotoUrl)}
        crossOrigin="use-credentials"
        alt={t('webAdmin.gallery.photoAlt', { locale: 'cs' })}
        className="shrink-0 rounded object-cover"
        style={{ width: 96, height: 72 }}
        loading="lazy"
        data-testid={`nemovitosti-cover-${row.id}`}
      />
    );

  /**
   * Krok SEZ1 bod 1 — „3/10" + rozbaleni se seznamem portalu a stavem.
   *
   * `<details>` zamerne, ne CSS tooltip: na mobilu 390 je to STITEK, ktery
   * jde rozbalit prstem (CSS `:hover` na dotykovem displeji neexistuje), na
   * desktopu nese tytez vety i v `title` pro najeti mysi. Tentyz prvek uz
   * takhle funguje v souhrnu na obrazovce Portaly.
   *
   * Nemovitost BEZ inzeratu neukazuje „0/10" (to by znamenalo „nikde nebezi,
   * i kdyz muze"), ale prazdny stav „Bez inzerátu".
   */
  const portalsCell = (row: PropertyListItem): React.JSX.Element => {
    const summary = portalsByProperty.get(row.id);
    if (summary === undefined) {
      return (
        <span className="text-sm whitespace-nowrap" style={{ color: 'var(--rh-text-muted)' }} data-testid={`nemovitosti-portals-none-${row.id}`}>
          {t('webAdmin.mvListings.portalsNone', { locale: 'cs' })}
        </span>
      );
    }
    const lines = summary.rows
      .map((item) => `${item.descriptor.name}: ${PORTAL_RUN_STATE_LABELS[item.runState]}`)
      .join('\n');
    return (
      <details data-testid={`nemovitosti-portals-${row.id}`}>
        <summary
          className={portalBadgeClass(summary)}
          style={{ cursor: 'pointer' }}
          title={lines}
          data-testid={`nemovitosti-portals-summary-${row.id}`}
        >
          {t('webAdmin.mvListings.portalsBadge', {
            locale: 'cs',
            values: { published: summary.published, total: summary.total },
          })}
        </summary>
        <ul className="rh-a2-facts">
          {summary.rows.map((item) => (
            <li key={item.descriptor.portal}>
              <span className="rh-a2-k">{item.descriptor.name}</span>
              <span className="rh-a2-v" data-testid={`nemovitosti-portal-${row.id}-${item.descriptor.portal}`}>
                {PORTAL_RUN_STATE_LABELS[item.runState]}
              </span>
            </li>
          ))}
        </ul>
      </details>
    );
  };

  const columns: readonly DataTableColumn<PropertyListItem>[] = [
    {
      key: 'select',
      header: t('webAdmin.mvListings.columnSelect', { locale: 'cs' }),
      render: (row) => (
        <input
          type="checkbox"
          checked={selected.has(row.id)}
          onChange={() => { toggleSelected(row.id); }}
          aria-label={t('webAdmin.mvListings.selectAriaLabel', {
            locale: 'cs',
            values: { name: row.name ?? row.address },
          })}
          data-testid={`nemovitosti-select-${row.id}`}
        />
      ),
    },
    {
      key: 'name',
      header: t('webAdmin.mvListings.columnTitle', { locale: 'cs' }),
      sortable: true,
      render: (row) => (
        /*
         * Nahled a nazev stoji v JEDNE bunce (krok UXF2 bod 5), ale nahled
         * NESMI byt UVNITR tlacitka: `MapPreview` je zivy Leaflet a ten do
         * sebe kresli vlastni odkazy (atribuce, ovladace). Tlacitko s nimi
         * uvnitr je `nested-interactive` (axe serious, zmereno sadou UXD4 na
         * `/nemovitosti` 3x) — ctecka takove tlacitko neohlasi a focus v nem
         * uvazne. Klikaci zustava NAZEV, nahled je vedle nej.
         */
        <span className="flex items-center gap-3" style={{ maxWidth: 420 }}>
          {/* Krok O6 → krok UXA1: nahled 96x72. Bez fotky NIKDY prazdna ikona
              (R.B. 1. 10.: „karty bez fotek — šedá ikona") — kdyz mame
              souradnice adresy, ukaze se mapa (`MapPreview`); ikona zustava
              jen pro adresu bez souradnic. Nikdy rozbity `<img>`. */}
          {cover(row)}
          <button
            type="button"
            className="block min-w-0 text-left"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit' }}
            title={row.name ?? row.address}
            onClick={() => { openDetail(row.id); }}
          >
            <span className="font-medium block truncate">{row.name ?? row.address}</span>
            <span className="text-sm block truncate" style={{ color: 'var(--rh-text-muted)' }}>
              {row.locality ?? row.address}
            </span>
          </button>
        </span>
      ),
    },
    {
      key: 'kind',
      header: t('webAdmin.mvListings.columnKind', { locale: 'cs' }),
      render: (row) => <span className="text-sm whitespace-nowrap">{kindLabel(row.kind)}</span>,
    },
    {
      key: 'stage',
      /*
       * Krok UXF2 bod 5: prazdna hodnota zustava PRAZDNA, ne „—“. Pomlcka
       * vypada jako udaj a radek s ni cte horsi nez radek bez ni.
       */
      header: t('webAdmin.caseListTexts.columnStatus', { locale: 'cs' }),
      render: (row) =>
        row.stage === null ? null : (
          <span className="rh-badge rh-badge--neutral whitespace-nowrap">{stageLabel(row.stage)}</span>
        ),
    },
    {
      /* Krok SEZ1 bod 1: stav exportu patri vedle stavu zakazky, pred cenu. */
      key: 'portals',
      header: t('webAdmin.portaly.portalsColumn', { locale: 'cs' }),
      render: portalsCell,
    },
    {
      key: 'price',
      header: t('webAdmin.mvListings.columnPrice', { locale: 'cs' }),
      sortable: true,
      render: (row) =>
        row.price === null ? null : <span className="text-sm whitespace-nowrap">{formatMoney(row.price)}</span>,
    },
    {
      key: 'updatedAt',
      header: t('webAdmin.mvListings.columnUpdated', { locale: 'cs' }),
      sortable: true,
      render: (row) => <span className="text-sm whitespace-nowrap">{formatDate(row.updatedAt)}</span>,
    },
  ];

  return (
    <div data-testid="nemovitosti-page">
      <PageHead
        title={t('webAdmin.mvListings.heading', { locale: 'cs' })}
        subtitle={
          list.data !== undefined ? (
            <span data-testid="nemovitosti-count">
              {t('webAdmin.mvListings.countSuffix', { locale: 'cs', values: { count: String(list.data.total) } })}
            </span>
          ) : undefined
        }
      />

      {(selected.size > 0 || batchState.kind === 'error') && (
        <div className="mb-4 flex flex-wrap items-baseline gap-3">
          {selected.size > 0 && (
            <button
              type="button"
              className="rh-btn rh-btn--primary"
              onClick={handleStartAuditBatch}
              disabled={batchState.kind === 'submitting'}
              data-testid="nemovitosti-start-audit-batch"
            >
              {batchState.kind === 'submitting'
                ? t('webAdmin.mvListings.startAuditSubmitting', { locale: 'cs' })
                : t('webAdmin.mvListings.startAuditButton', { locale: 'cs', values: { count: String(selected.size) } })}
            </button>
          )}
          {batchState.kind === 'error' && (
            <span role="alert" className="text-sm" style={{ color: 'var(--rh-danger)' }}>
              {t('webAdmin.mvListings.startAuditErrorPrefix', { locale: 'cs', values: { message: batchState.message } })}
            </span>
          )}
        </div>
      )}

      {/* Krok SR1 bod 4: hromadne akce nad vyberem — s nahledem dopadu. */}
      {selected.size > 0 && (
        <BulkPortalActions
          propertyIds={[...selected]}
          onDone={() => {
            void list.refetch();
          }}
        />
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            className="rh-input"
            value={searchValue}
            placeholder={t('webAdmin.mvListings.searchPlaceholder', { locale: 'cs' })}
            aria-label={t('webAdmin.mvListings.searchAriaLabel', { locale: 'cs' })}
            onChange={(e) => { patchQuery({ search: e.target.value }); }}
            data-testid="nemovitosti-search"
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          {t('webAdmin.mvListings.localityLabel', { locale: 'cs' })}
          <select
            className="border rounded px-2 py-1 text-sm"
            value={localityFilter}
            onChange={(e) => { setFilter('locality', e.target.value); }}
            data-testid="nemovitosti-locality"
          >
            <option value="">{t('webAdmin.mvListings.all', { locale: 'cs' })}</option>
            {(localities.data ?? []).map((locality) => (
              <option key={locality} value={locality}>{locality}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          {t('webAdmin.mvListings.columnKind', { locale: 'cs' })}
          <select
            className="border rounded px-2 py-1 text-sm"
            value={kindFilter}
            onChange={(e) => { setFilter('kind', e.target.value); }}
            data-testid="nemovitosti-kind"
          >
            <option value="">{t('webAdmin.mvListings.all', { locale: 'cs' })}</option>
            {KIND_VALUES.map((kind) => (
              <option key={kind} value={kind}>{kindLabel(kind)}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          {t('webAdmin.caseListTexts.columnStatus', { locale: 'cs' })}
          <select
            className="border rounded px-2 py-1 text-sm"
            value={stageFilter}
            onChange={(e) => { setFilter('stage', e.target.value); }}
            data-testid="nemovitosti-stage"
          >
            <option value="">{t('webAdmin.mvListings.all', { locale: 'cs' })}</option>
            {LISTING_STAGES.map((stage) => (
              <option key={stage} value={stage}>{stageLabel(stage)}</option>
            ))}
          </select>
        </label>
      </div>

      <DataTable
        columns={columns}
        {...(list.data !== undefined ? { data: list.data } : {})}
        query={query}
        onQueryChange={patchQuery}
        loading={list.isLoading}
        {...(list.isError ? { error: errorMessage(list.error), onRetry: () => { void list.refetch(); } } : {})}
        texts={TABLE_TEXTS}
        caption={t('webAdmin.mvListings.tableCaption', { locale: 'cs' })}
        onExport={() => { void downloadPropertiesCsv(query); }}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        emptyState={
          <EmptyState
            title={t('webAdmin.mvListings.noMatchTitle', { locale: 'cs' })}
            description={t('webAdmin.mvListings.noMatchDescription', { locale: 'cs' })}
            /*
              Krok QAFIX2 (nalez N03): prazdny seznam je presne to misto, kde
              makler z jine kancelare potrebuje „prenest nabidky odjinud" —
              pruvodce `/import` byl do QAFIX2 bez jakehokoli vstupniho bodu.
            */
            action={
              /*
                Odkaz je `<a href>`, ne `<Link>` — stejne jako „Nastavení
                kanceláře" na /nastaveni. Tenhle modul ma v testech atrapu
                `@tanstack/react-router` bez `Link`, a merit se tu ma seznam
                nemovitosti, ne router.
              */
              <a href="/import" className="rh-btn" data-testid="nemovitosti-import-link">
                {t('webAdmin.officeMigration.propertiesEmptyAction', { locale: 'cs' })}
              </a>
            }
          />
        }
        rowKey={(row) => row.id}
      />
    </div>
  );
}
